import { AllowedAMC, FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import {
  AmcFeeEstimate,
  AppraisalDetailsV2Service,
  EstimateAmcFeesRequest,
} from '../../../services/documentForms/appraisalDetailsV2/appraisalDetailsV2Service';
import { FileUpload } from '@roc/ui';
import { LoanStore } from '@roc/feature-loans';
import { ValuationProperty } from './appraisalDetailsV2Store';
import { AmcOption, amcToOption, filterAmcsByFee } from './amcFilterRules';
import { LoanSubType } from '@roc/feature-utils';

const appraisalQuestionnaireForm = {
  fields: {
    providingOwnAppraisal: {
      value: null,
      error: null,
      rule: '',
    },
    selectedAmc: {
      value: '',
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class AppraisalQuestionnaireV2FormStore extends FormStore {
  public globalStore: GlobalStore;
  private loanStore: LoanStore;
  private appraisalService: AppraisalDetailsV2Service;
  public pdfUploads: Record<string, FileUpload> = {};
  public xmlUploads: Record<string, FileUpload> = {};
  public amcOptions: AmcOption[] = [];
  public valuationReports: Record<string, { propertyUUID: string; reportPath?: string; xmlPath?: string }> = {};

  constructor(globalStore: GlobalStore, loanStore?: LoanStore) {
    super(appraisalQuestionnaireForm, globalStore);
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.appraisalService = new AppraisalDetailsV2Service();
    makeObservable(this, {
      onFormSubmit: flow,
      uploadValuationReport: flow,
      pdfUploads: observable,
      xmlUploads: observable,
      setPropertyPdfUpload: action,
      setPropertyXmlUpload: action,
      amcOptions: observable,
      getAmcOptions: flow,
      valuationReports: observable,
      showAMCDropdown: computed,
    });
  }

  setPropertyPdfUpload(propertyUUID: any, file: FileUpload | null) {
    this.pdfUploads = { ...this.pdfUploads, [propertyUUID]: file };
  }

  setPropertyXmlUpload(propertyUUID: any, file: FileUpload | null) {
    this.xmlUploads = { ...this.xmlUploads, [propertyUUID]: file };
  }

  *getAmcOptions(request?: EstimateAmcFeesRequest) {
    const isInternal = this.globalStore.isInternalPortal ?? false;
    const req: EstimateAmcFeesRequest = request ?? {
      lenderId: this.loanStore?.loanDetails?.lenderId,
      loanSubtype: this.loanStore?.loanDetails?.loanSubType,
      properties: (this.loanStore?.loanDetails?.properties ?? []).map((p: any) => ({
        state: p.state,
        county: p.county,
        propertyType: p.propertyType,
        unitCount: p.units,
      })),
    };
    const isRentalTerm = req.loanSubtype === LoanSubType.SINGLE_PROPERTY || req.loanSubtype === LoanSubType.RENTAL_PORTFOLIO;
    try {
      const response = yield this.appraisalService.estimateAmcFees(req);
      this.amcOptions = (response.data as AmcFeeEstimate[])
        .filter(amc => {
          if (amc.amcName === 'Internal Valuation') return isInternal && !isRentalTerm;
          return amc.estimatedFee > 0;
        })
        .map(amcToOption);
    } catch {
      const amcsAllowed = this.globalStore.userFeatures?.amcsAllowed;
      this.amcOptions = (amcsAllowed || [])
        .filter((amc: AllowedAMC) => !!amc.amcId && amc.name?.toLowerCase() !== 'other')
        .map((amc: AllowedAMC) => ({ label: amc.name, value: amc.name, estimatedFee: 0 }));
    }
  }

  validateQuestionnaire(properties: ValuationProperty[]): boolean {
    const otherAmcAllowed = this.globalStore.userFeatures?.otherAmcAllowed;

    if (otherAmcAllowed && this.form.fields.providingOwnAppraisal.value === null) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fill out the Valuation section.',
      });
      return false;
    }
    if (this.form.fields.providingOwnAppraisal.value !== true && !this.form.fields.selectedAmc.value) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fill out the Valuation section.',
      });
      return false;
    }
    return true;
  }

  *onFormSubmit(properties?: ValuationProperty[], callback?: () => void) {
    if (!this.validateQuestionnaire(properties ?? [])) {
      throw new Error();
    }

    try {
      const request = {
        ...this.getFormValues(),
        loanId: this.loanStore?.loanDetails?.loanId,
      }
      yield this.appraisalService.saveQuestionnaire(request);
      if (this.form.fields.providingOwnAppraisal.value === true) {
        const uploadRequests: any[] = [];
        properties?.forEach(property => {
          const uploadRequestData = {
            propertyId: property.propertyId,
            loanId: this.loanStore?.loanDetails?.loanId
          }
          const formData = new FormData();
          formData.append("data", new Blob([JSON.stringify(uploadRequestData)], { type: "application/json" }));
          formData.append("pdf", this.pdfUploads[property.propertyId] ? this.getFileFromFileUpload(this.pdfUploads[property.propertyId]) : null);
          formData.append("xml", this.xmlUploads[property.propertyId] ? this.getFileFromFileUpload(this.xmlUploads[property.propertyId]) : null);
          uploadRequests.push(this.appraisalService.saveAppraisalQuestionnaireDocument(formData));
        });

        yield Promise.all(uploadRequests);
      }
      if (callback) {
        callback();
      }
      this.reset();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Appraisal questionnaire saved successfully',
      });
    } catch (error) {
      console.error('Error saving appraisal questionnaire', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to save appraisal questionnaire',
      });
      throw new Error();
    }
  }

  private getFileFromFileUpload(fileUpload: FileUpload): File {
    const blob = fileUpload.file.slice(0, fileUpload.file.size, fileUpload.file.type);
    return new File([blob], fileUpload.file.name, { type: fileUpload.file.type });
  }

  *uploadValuationReport(file: FileUpload, fileType: string, loanQuoteId: number, propertyUUID: string) {
    try {
      const formData = new FormData();
      formData.append('file', file.file, file.name);
      formData.append('fileType', fileType);
      const response = yield this.appraisalService.uploadValuationReport(formData, loanQuoteId, propertyUUID);
      const path: string = response.data.data;
      const existing = this.valuationReports[propertyUUID] ?? { propertyUUID: propertyUUID };
      if (fileType === 'pdf') {
        this.valuationReports[propertyUUID] = { ...existing, reportPath: path };
      } else {
        this.valuationReports[propertyUUID] = { ...existing, xmlPath: path };
      }
    } catch (error) {
      console.error('Error uploading valuation report', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to upload valuation report',
      });
    }
  }

  getValuationQuestionnaire() {
    const values = this.getFormValues();
    return {
      providingOwnAppraisal: values.providingOwnAppraisal,
      selectedAmc: values.selectedAmc,
      valuationReportList: Object.values(this.valuationReports),
    };
  }

  reset() {
    super.reset();
    this.pdfUploads = {};
    this.xmlUploads = {};
    this.valuationReports = {};
  }

  private validateUploads(properties: ValuationProperty[]) {
    return properties.every(property => {
      const key = property.uuid ?? property.propertyId;
      const pdfUpload = this.pdfUploads[key];
      const xmlUpload = this.xmlUploads[key];
      return pdfUpload && xmlUpload;
    });
  }


  get showAMCDropdown() {
    return this.form.fields.providingOwnAppraisal.value === false;
  }

}
